import { useState } from "react";

import { Check, Copy } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Monospace code / formula block with an optional eyebrow label and a
 * copy-to-clipboard button. Reach for this to display a stored equation, a SQL
 * query, or any snippet the user might want to lift verbatim — it scrolls
 * horizontally rather than wrapping, so long lines never break the layout.
 *
 * `tone` tints the left rail + label: `brand` (cyan, for equations/formulas),
 * `neutral` (default, for queries/snippets).
 */
export interface CodeBlockProps {
  code: string;
  label?: string;
  tone?: "neutral" | "brand";
  /** Show the copy button (default true). */
  copyable?: boolean;
  className?: string;
}

export function CodeBlock({
  code,
  label,
  tone = "neutral",
  copyable = true,
  className,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-md border bg-muted/60",
        tone === "brand" ? "border-l-2 border-l-brand" : "border-l-2 border-l-border-strong",
        className,
      )}
    >
      {label ? (
        <div
          className={cn(
            "px-3 pt-2 text-[10px] font-semibold uppercase tracking-wider",
            tone === "brand" ? "text-brand" : "text-muted-foreground",
          )}
        >
          {label}
        </div>
      ) : null}
      <pre className="overflow-x-auto px-3 py-2 text-xs leading-relaxed">
        <code className="font-mono text-foreground">{code}</code>
      </pre>
      {copyable ? (
        <button
          type="button"
          onClick={copy}
          aria-label={copied ? "Copied" : "Copy to clipboard"}
          className="absolute right-1.5 top-1.5 rounded p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-success" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      ) : null}
    </div>
  );
}
